import { KazokugotoService } from '@/services';
import { ENDPOINT } from '@/services/endpoint';
import { IResponseError } from '@/types/common';
import {
  IKazokugotoJobItemSummary,
  KazokugotoEdition,
} from '@/types/response';
import {
  notificationMessage,
  NOTIFY_STATUS,
} from '@/utils';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  DatePicker,
  Form,
  Space,
  Spin,
  Steps,
  Tag,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ManzaiFlowDiagram } from './ManzaiFlowDiagram';
import { ArticleFlowDiagram } from './ArticleFlowDiagram';
import { useMutation, useQuery } from 'react-query';

const { Title, Paragraph, Text } = Typography;

const EDITION_LABEL: Record<KazokugotoEdition, string> = {
  morning: '朝刊',
  evening: '夕刊',
  manzai: '漫才（一言）',
};

interface IProps {
  title?: string;
}

export const KazokugotoGenerate: React.FC<IProps> = ({ title }) => {
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [skipCover, setSkipCover] = useState<boolean>(false);
  const [jobId, setJobId] = useState<number | null>(null);

  const generateMutation = useMutation(
    () =>
      KazokugotoService.startGeneration({
        publishDate: date!.format('YYYY-MM-DD'),
        skipCover,
      }),
    {
      onSuccess: (response) => {
        setJobId(response.data.jobId);
        notificationMessage({
          message:
            '生成を開始しました。完了までこのページでお待ちください（数分かかります）',
          type: NOTIFY_STATUS.SUCCESS,
        });
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: `生成の開始に失敗しました: ${error.message ?? 'unknown error'}`,
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  // ジョブをポーリング（pending/running の間は 3 秒ごと）
  const { data: jobData } = useQuery(
    [ENDPOINT.KAZOKUGOTO.GENERATION_JOB, jobId],
    () => KazokugotoService.getGenerationJob(jobId!),
    {
      enabled: !!jobId,
      refetchInterval: (resp) => {
        const s = resp?.data.status;
        return s === 'pending' || s === 'running' ? 3000 : false;
      },
    },
  );
  const job = jobData?.data;
  const isJobRunning =
    job?.status === 'pending' || job?.status === 'running';

  const regenerateCoverMutation = useMutation(
    (itemId: number) => KazokugotoService.regenerateCover(itemId),
    {
      onSuccess: (response) => {
        notificationMessage({
          message: `カバー画像の生成を開始しました (item id=${response.data.id})。完了は「アイテム一覧」で確認できます`,
          type: NOTIFY_STATUS.SUCCESS,
        });
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: `カバー再生成の開始に失敗しました: ${error.message ?? 'unknown error'}`,
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  const handleGenerate = () => {
    if (!date) {
      notificationMessage({
        message: '配信日を選択してください',
        type: NOTIFY_STATUS.WARNING,
      });
      return;
    }
    generateMutation.mutate();
  };

  const renderSummary = (
    edition: KazokugotoEdition,
    item: IKazokugotoJobItemSummary | null,
  ) => {
    const label = EDITION_LABEL[edition];
    if (!item) {
      return (
        <Card title={label} size='small' style={{ marginBottom: 16 }}>
          <Text type='secondary'>未生成 / 失敗</Text>
        </Card>
      );
    }
    const coverLabel: Record<string, { text: string; color: string }> = {
      ready: { text: '生成済み', color: 'green' },
      generating: { text: '生成中', color: 'blue' },
      failed: { text: '失敗', color: 'red' },
      none: { text: 'なし', color: 'default' },
    };
    const cov = coverLabel[item.coverStatus] ?? coverLabel.none;
    return (
      <Card
        title={
          <Space>
            <span>{label}</span>
            <Tag color={item.status === 'published' ? 'green' : 'orange'}>
              {item.status}
            </Tag>
            <Text type='secondary' style={{ fontSize: 12 }}>
              id={item.id}
            </Text>
          </Space>
        }
        size='small'
        style={{ marginBottom: 16 }}
        extra={
          <Button
            size='small'
            onClick={() => regenerateCoverMutation.mutate(item.id)}
            loading={
              regenerateCoverMutation.isLoading &&
              regenerateCoverMutation.variables === item.id
            }
          >
            カバー再生成
          </Button>
        }
      >
        <Title level={5} style={{ marginTop: 0 }}>
          {item.title}
        </Title>
        <Space wrap size={[8, 4]}>
          <Text type='secondary'>カバー画像: </Text>
          <Tag color={cov.color}>{cov.text}</Tag>
        </Space>
      </Card>
    );
  };

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <header>
          <Title level={3} style={{ marginTop: 0 }}>
            かぞくごと。生成
          </Title>
          <Paragraph type='secondary' style={{ marginBottom: 16 }}>
            日付を指定して朝刊・夕刊・漫才を一括生成します。既に同日のアイテムが
            あれば上書きされます。生成には数分かかります。
          </Paragraph>
        </header>

        <Collapse
          items={[
            {
              key: 'article-flow',
              label: '朝刊・夕刊の生成フロー（仕組み）— どのプロンプトでいつ LLM が走るか',
              children: <ArticleFlowDiagram />,
            },
            {
              key: 'manzai-flow',
              label: '漫才の生成フロー（仕組み）— どのプロンプトでいつ LLM が走るか',
              children: <ManzaiFlowDiagram />,
            },
          ]}
        />

        <Card>
          <Form layout='inline'>
            <Form.Item label='配信日' required>
              <DatePicker
                value={date}
                onChange={(value) => setDate(value)}
                format='YYYY-MM-DD'
                allowClear={false}
              />
            </Form.Item>
            <Form.Item>
              <Checkbox
                checked={skipCover}
                onChange={(e) => setSkipCover(e.target.checked)}
              >
                カバー画像をスキップ（テキストのみ）
              </Checkbox>
            </Form.Item>
            <Form.Item>
              <Button
                type='primary'
                onClick={handleGenerate}
                loading={generateMutation.isLoading || isJobRunning}
                disabled={isJobRunning}
              >
                {isJobRunning ? '生成中…' : '生成する'}
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {job && isJobRunning && (
          <Card>
            <Space direction='vertical' style={{ width: '100%' }} size='middle'>
              <Space>
                <Spin />
                <Text>
                  生成中です（{job.publishDate}）。完了までこのページでお待ちください…
                </Text>
              </Space>
              {job.progress && job.progress.steps.length > 0 && (
                <Steps
                  size='small'
                  direction='vertical'
                  items={job.progress.steps.map((s) => ({
                    title: s.label,
                    status:
                      s.status === 'done'
                        ? 'finish'
                        : s.status === 'running'
                          ? 'process'
                          : s.status === 'failed'
                            ? 'error'
                            : 'wait',
                    description:
                      s.status === 'running'
                        ? '生成中…'
                        : s.status === 'failed'
                          ? '失敗'
                          : undefined,
                  }))}
                />
              )}
            </Space>
          </Card>
        )}

        {job && job.status === 'failed' && (
          <Alert
            type='error'
            showIcon
            message='生成ジョブが失敗しました'
            description={job.error ?? undefined}
          />
        )}

        {job && job.status === 'done' && job.result && (
          <>
            {(job.result.errors?.length ?? 0) > 0 && (
              <Alert
                type='warning'
                message='一部のエディションが失敗しました'
                description={
                  <ul style={{ marginBottom: 0 }}>
                    {job.result.errors.map((e, idx) => (
                      <li key={idx}>
                        <Text strong>{EDITION_LABEL[e.edition]}:</Text>{' '}
                        {e.message}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
            <div>
              <Title level={4}>結果 ({job.result.publishDate})</Title>
              {renderSummary('morning', job.result.morning)}
              {renderSummary('evening', job.result.evening)}
              {renderSummary('manzai', job.result.manzai)}
              <Paragraph type='secondary'>
                本文・カバー画像の詳細は「アイテム一覧」で確認できます。
              </Paragraph>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
